import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

export type PlatformStats = {
  components: number;
  apis: number;
  resources: number;
  templates: number;
  systems: number;
  loading: boolean;
  error?: Error;
};

const FALLBACK: Omit<PlatformStats, 'loading' | 'error'> = {
  components: 0,
  apis: 0,
  resources: 0,
  templates: 0,
  systems: 0,
};

/**
 * Fetches lightweight catalog counts for the landing-page stats strip.
 * Fails open: returns zeros and `error` when the catalog backend is
 * unreachable (e.g. unauthenticated visitors) so the UI can still render.
 */
export const usePlatformStats = (): PlatformStats => {
  const catalogApi = useApi(catalogApiRef);
  const [state, setState] = useState<PlatformStats>({
    ...FALLBACK,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    let firstFailure: Error | undefined;

    const countKind = async (kind: string): Promise<number> => {
      try {
        const res = await catalogApi.getEntities({
          filter: { kind },
          fields: ['metadata.name'],
        });
        return res.items.length;
      } catch (e) {
        firstFailure ??= e as Error;
        return 0;
      }
    };

    Promise.all([
      countKind('Component'),
      countKind('API'),
      countKind('Resource'),
      countKind('Template'),
      countKind('System'),
    ]).then(([components, apis, resources, templates, systems]) => {
      if (cancelled) return;
      setState({
        components,
        apis,
        resources,
        templates,
        systems,
        loading: false,
        error: firstFailure,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  return state;
};
